// Provider choice is a durable input to the Grok Build route, never a cosmetic
// display preference. Nous remains the default stealth path when no provider is
// selected explicitly.
export type HarperProvider = "nous" | "openai" | "anthropic" | "moonshot";
export type ProviderIconName = "openai" | "anthropic" | "moonshot" | "chip";

export interface ProviderDef {
  id: HarperProvider;
  label: string;
  model: string;
  modelId: string;
  sub: string;
  icon: ProviderIconName;
  managed: boolean;
}

export const HARPER_ACTIVE_PROVIDER_STORAGE_KEY =
  "fintheon:active-chat-provider";
// Retained for archived compatibility only. Ordinary Fintheon chat never reads or writes it.
export const HARPER_LEGACY_PROVIDER_STORAGE_KEY = "fintheon:harper-provider";
export const DEFAULT_CHAT_PROVIDER_STORAGE_KEY =
  "fintheon:default-chat-provider";
export const HARPER_PROVIDER_STORAGE_KEY = HARPER_ACTIVE_PROVIDER_STORAGE_KEY;
export const HARPER_PROVIDER_DEFAULT_CHANGED_EVENT =
  "fintheon:harper-provider-default-changed";
export const HARPER_PROVIDER_ACTIVE_CHANGED_EVENT =
  "fintheon:harper-provider-active-changed";
export const HARPER_PROVIDER_VISIBILITY_CHANGED_EVENT =
  "fintheon:harper-provider-visibility-changed";
export const HIDDEN_CHAT_PROVIDERS_STORAGE_KEY =
  "fintheon:hidden-chat-providers";
export const OPENAI_KEY_STATUS = "fintheon:openai-key-status";
export const ANTHROPIC_KEY_STATUS = "fintheon:anthropic-key-status";
export const MOONSHOT_KEY_STATUS = "fintheon:moonshot-key-status";

export const PROVIDERS: ProviderDef[] = [
  {
    id: "nous",
    label: "Nous Portal",
    model: "Dynamic free catalog",
    modelId: "dynamic-free-catalog",
    sub: "Default",
    icon: "chip",
    managed: false,
  },
  {
    id: "openai",
    label: "OpenAI",
    model: "GPT 5.6 Sol / Terra",
    modelId: "gpt-5.6-sol",
    sub: "Codex subscription",
    icon: "openai",
    managed: false,
  },
  {
    id: "anthropic",
    label: "Anthropic",
    model: "Opus 4.8 / Sonnet 5",
    modelId: "claude-opus-4-8",
    sub: "API key",
    icon: "anthropic",
    managed: false,
  },
  {
    id: "moonshot",
    label: "Moonshot",
    model: "Kimi K3",
    modelId: "kimi-k3",
    sub: "API key",
    icon: "moonshot",
    managed: false,
  },
];

export const DEFAULT_INTERACTIVE_CHAT_PROVIDER = "nous" as const;
export const DEFAULT_INTERACTIVE_CHAT_MODEL = "dynamic-free-catalog";
// Kept for consumers that need a synchronous browser-less fallback.
export const INTERACTIVE_CHAT_PROVIDER = DEFAULT_INTERACTIVE_CHAT_PROVIDER;
export const INTERACTIVE_CHAT_MODEL = DEFAULT_INTERACTIVE_CHAT_MODEL;
export const INTERACTIVE_CHAT_PROVIDERS = PROVIDERS;

export function readInteractiveChatProvider(): HarperProvider {
  const selected = readStoredHarperProvider();
  return INTERACTIVE_CHAT_PROVIDERS.some((provider) => provider.id === selected)
    ? selected
    : DEFAULT_INTERACTIVE_CHAT_PROVIDER;
}

export function readInteractiveChatModel(provider: HarperProvider): string {
  return readStoredProviderModel(provider) ?? defaultModelForProvider(provider);
}

export function readInteractiveChatSelection(): {
  provider: HarperProvider;
  model: string;
} {
  const provider = readInteractiveChatProvider();
  return { provider, model: readInteractiveChatModel(provider) };
}

const READY_PROVIDER_STATUSES = new Set([
  "set",
  "oauth",
  "ok",
  "ready",
  "configured",
  "authenticated",
]);

const PROVIDER_MISSING_MESSAGES: Record<HarperProvider, string> = {
  openai: "Connect OpenAI",
  nous: "Connect Nous Portal",
  anthropic: "Connect Anthropic",
  moonshot: "Connect Moonshot",
};

export function providerDisplayName(provider: ProviderDef): string {
  return provider.label;
}

export function providerDefaultSubtext(provider: ProviderDef): string {
  if (provider.id === "nous") return provider.sub;
  return provider.model;
}

export function readProviderStatusMessage(
  provider: HarperProvider,
): string | null {
  const raw = readStorage(providerStatusKey(provider));
  if (!raw) return null;
  const normalized = raw.trim().toLowerCase();
  if (!normalized || READY_PROVIDER_STATUSES.has(normalized)) return null;
  if (
    normalized === "missing" ||
    normalized === "unset" ||
    normalized === "not-set"
  ) {
    return PROVIDER_MISSING_MESSAGES[provider];
  }
  return raw
    .replace(/^error[:\s-]*/i, "")
    .replace(/[_-]+/g, " ")
    .trim();
}

export function providerSubtext(provider: ProviderDef): string {
  return (
    readProviderStatusMessage(provider.id) ?? providerDefaultSubtext(provider)
  );
}

export function providerHasStatusIssue(provider: HarperProvider): boolean {
  return readProviderStatusMessage(provider) != null;
}

export function normalizeHarperProvider(raw: string | null): HarperProvider {
  const value = raw?.trim().toLowerCase();
  if (!value) return DEFAULT_INTERACTIVE_CHAT_PROVIDER;
  if (
    value === "openai" ||
    value === "openai-oauth" ||
    value === "openai-codex" ||
    value.startsWith("openai/")
  ) {
    return "openai";
  }
  if (value === "anthropic" || value.startsWith("claude")) return "anthropic";
  if (value === "moonshot" || value.startsWith("kimi")) return "moonshot";
  if (
    value === "nous" ||
    value === "nous-portal" ||
    value.startsWith("nous/")
  ) {
    return "nous";
  }
  return DEFAULT_INTERACTIVE_CHAT_PROVIDER;
}

export function providerStatusKey(provider: HarperProvider): string {
  if (provider === "openai") return OPENAI_KEY_STATUS;
  if (provider === "nous") return "fintheon:nous-provider-status";
  if (provider === "anthropic") return ANTHROPIC_KEY_STATUS;
  return MOONSHOT_KEY_STATUS;
}

export function providerModelStorageKey(provider: HarperProvider): string {
  return `fintheon:${provider}-model`;
}

export function readStoredProviderModel(
  provider: HarperProvider,
): string | null {
  return readStorage(providerModelStorageKey(provider));
}

export function writeStoredProviderModel(
  provider: HarperProvider,
  model: string,
): void {
  const value = model.trim();
  if (!value) return;
  writeStorage(providerModelStorageKey(provider), value);
}

export function readHiddenChatProviders(): HarperProvider[] {
  const raw = readStorage(HIDDEN_CHAT_PROVIDERS_STORAGE_KEY);
  if (!raw) return [];
  try {
    const values = JSON.parse(raw);
    if (!Array.isArray(values)) return [];
    const valid = new Set(
      INTERACTIVE_CHAT_PROVIDERS.map((provider) => provider.id),
    );
    return Array.from(
      new Set(
        values.filter(
          (value): value is HarperProvider =>
            typeof value === "string" && valid.has(value as HarperProvider),
        ),
      ),
    );
  } catch {
    return [];
  }
}

export function isChatProviderHidden(provider: HarperProvider): boolean {
  return readHiddenChatProviders().includes(provider);
}

export function toggleChatProviderVisibility(
  provider: HarperProvider,
): HarperProvider[] {
  const hidden = new Set(readHiddenChatProviders());
  if (hidden.has(provider)) {
    hidden.delete(provider);
  } else if (hidden.size < INTERACTIVE_CHAT_PROVIDERS.length - 1) {
    hidden.add(provider);
  }
  const next = [...hidden];
  writeStorage(HIDDEN_CHAT_PROVIDERS_STORAGE_KEY, JSON.stringify(next));
  dispatchProviderEvent(HARPER_PROVIDER_VISIBILITY_CHANGED_EVENT, provider);
  return next;
}

export function readStoredHarperProvider(
  defaultProvider?: string | null,
): HarperProvider {
  const active = readStorage(HARPER_ACTIVE_PROVIDER_STORAGE_KEY);
  if (active) return normalizeHarperProvider(active);
  const defaultValue =
    defaultProvider ?? readStorage(DEFAULT_CHAT_PROVIDER_STORAGE_KEY);
  if (defaultValue) return normalizeHarperProvider(defaultValue);
  return DEFAULT_INTERACTIVE_CHAT_PROVIDER;
}

export function readDefaultHarperProvider(
  defaultProvider?: string | null,
): HarperProvider {
  const defaultValue =
    defaultProvider ?? readStorage(DEFAULT_CHAT_PROVIDER_STORAGE_KEY);
  if (defaultValue) return normalizeHarperProvider(defaultValue);
  return DEFAULT_INTERACTIVE_CHAT_PROVIDER;
}

export function hasActiveHarperProvider(): boolean {
  return Boolean(readStorage(HARPER_ACTIVE_PROVIDER_STORAGE_KEY));
}

export function writeActiveHarperProvider(provider: HarperProvider): void {
  writeStorage(HARPER_ACTIVE_PROVIDER_STORAGE_KEY, provider);
  dispatchProviderEvent(HARPER_PROVIDER_ACTIVE_CHANGED_EVENT, provider);
}

export function writeDefaultHarperProvider(provider: HarperProvider): void {
  writeStorage(DEFAULT_CHAT_PROVIDER_STORAGE_KEY, provider);
  writeStorage(HARPER_ACTIVE_PROVIDER_STORAGE_KEY, provider);
  dispatchProviderEvent(HARPER_PROVIDER_DEFAULT_CHANGED_EVENT, provider);
  dispatchProviderEvent(HARPER_PROVIDER_ACTIVE_CHANGED_EVENT, provider);
}

export function resetActiveHarperProviderToDefault(
  defaultProvider?: string | null,
): HarperProvider {
  const provider = readDefaultHarperProvider(defaultProvider);
  writeActiveHarperProvider(provider);
  return provider;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function dispatchProviderEvent(type: string, provider: HarperProvider): void {
  try {
    window.dispatchEvent(new CustomEvent(type, { detail: { provider } }));
  } catch {
    /* ignore */
  }
}

function defaultModelForProvider(provider: HarperProvider): string {
  return (
    PROVIDERS.find((entry) => entry.id === provider)?.modelId ??
    DEFAULT_INTERACTIVE_CHAT_MODEL
  );
}
